import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { SizedResponsiveContainer } from '@/shared/components/charts/SizedResponsiveContainer';
import { EmptyPanel, ErrorPanel } from '@/shared/components/dashboard/panels';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import type { SubscriptionEventAnalytics } from '../types';
import { subscriptionEventTypeLabelKey } from '../utils/eventLabels';
import { recordToChartData } from '../utils/helpers';

interface SubscriptionEventAnalyticsSectionProps {
  analytics: SubscriptionEventAnalytics | undefined;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
}

function ChartSkeleton() {
  return <div className="h-72 rounded-lg bg-muted/40 animate-pulse" />;
}

export function SubscriptionEventAnalyticsSection({
  analytics,
  isLoading,
  isError,
  errorMessage,
}: SubscriptionEventAnalyticsSectionProps) {
  const { t } = useTranslation();

  const eventsByType = useMemo(
    () => (analytics ? recordToChartData(analytics.eventsByType) : []),
    [analytics],
  );

  const eventsBySource = useMemo(
    () => (analytics ? recordToChartData(analytics.eventsBySource) : []),
    [analytics],
  );

  const last30DaysByType = useMemo(
    () => (analytics ? recordToChartData(analytics.last30Days.eventsByType) : []),
    [analytics],
  );

  const typeChartData = useMemo(
    () =>
      eventsByType.map((row) => ({
        ...row,
        label: t(subscriptionEventTypeLabelKey(row.name), { defaultValue: row.name }),
      })),
    [eventsByType, t],
  );

  const sourceChartData = useMemo(
    () =>
      eventsBySource.map((row) => ({
        ...row,
        label: t(`subscriptionEvents.eventSources.${row.name}`, { defaultValue: row.name }),
      })),
    [eventsBySource, t],
  );

  const last30ChartData = useMemo(
    () =>
      last30DaysByType.map((row) => ({
        ...row,
        label: t(subscriptionEventTypeLabelKey(row.name), { defaultValue: row.name }),
      })),
    [last30DaysByType, t],
  );

  if (isLoading && !analytics) {
    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartSkeleton />
        <ChartSkeleton />
        <ChartSkeleton />
        <ChartSkeleton />
      </div>
    );
  }

  if (isError && !analytics) {
    return <ErrorPanel message={errorMessage ?? t('subscriptionEvents.errors.loadFailed')} />;
  }

  if (!analytics) {
    return null;
  }

  const hasAnalyticsData =
    eventsByType.length > 0 ||
    eventsBySource.length > 0 ||
    analytics.topProducts.length > 0 ||
    last30DaysByType.length > 0;

  if (!hasAnalyticsData) {
    return (
      <EmptyPanel
        title={t('subscriptionEvents.analytics.emptyTitle')}
        message={t('subscriptionEvents.analytics.emptyMessage')}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('subscriptionEvents.analytics.eventsByType')}</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            {typeChartData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                {t('subscriptionEvents.analytics.noData')}
              </p>
            ) : (
              <SizedResponsiveContainer height={288} className="w-full min-w-0">
                <BarChart data={typeChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(value) => String(value ?? 0)}
                    labelFormatter={(label) => String(label)}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </SizedResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('subscriptionEvents.analytics.eventsBySource')}</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            {sourceChartData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                {t('subscriptionEvents.analytics.noData')}
              </p>
            ) : (
              <SizedResponsiveContainer height={288} className="w-full min-w-0">
                <BarChart data={sourceChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value) => String(value ?? 0)}
                    labelFormatter={(label) => String(label)}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </SizedResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('subscriptionEvents.analytics.topProducts')}</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.topProducts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                {t('subscriptionEvents.analytics.noData')}
              </p>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-start px-4 py-2 font-medium">
                        {t('subscriptionEvents.analytics.product')}
                      </th>
                      <th className="text-end px-4 py-2 font-medium">
                        {t('subscriptionEvents.analytics.eventCount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.topProducts.map((row) => (
                      <tr key={row.productId} className="border-t">
                        <td className="px-4 py-2">
                          <span className="font-mono text-xs">
                            {row.productSlug ?? row.productId}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-end tabular-nums">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('subscriptionEvents.analytics.last30Days')}</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 space-y-4">
            <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
              <p className="text-xs font-medium text-muted-foreground">
                {t('subscriptionEvents.kpi.last30DaysEvents')}
              </p>
              <p className="text-2xl font-bold tabular-nums">{analytics.last30Days.totalEvents}</p>
            </div>
            {last30ChartData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('subscriptionEvents.analytics.noData')}
              </p>
            ) : (
              <SizedResponsiveContainer height={220} className="w-full min-w-0">
                <BarChart data={last30ChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 9 }}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value) => String(value ?? 0)}
                    labelFormatter={(label) => String(label)}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </SizedResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
